import { COUNTRIES_MAP } from '@/const/countries.const';

/**
 * Country, language and list texts from `Intl`, with fallbacks for the
 * admin's browser floor (Chrome 64 / Safari 12 have neither
 * `Intl.DisplayNames` nor `Intl.ListFormat`). The globals are read on every
 * call, so a missing API is detected wherever the code runs.
 */

interface IListFormatter {
  format(items: string[]): string;
}

type TListFormatConstructor = new (
  locale: string,
  options: { type: 'conjunction' }
) => IListFormatter;

/** `Intl.ListFormat` is not in the project's TypeScript lib (ES2021). */
function isListFormatConstructor(
  value: unknown
): value is TListFormatConstructor {
  return typeof value === 'function';
}

function displayName(
  type: 'region' | 'language',
  code: string,
  locale: string
): string | null {
  if (typeof Intl.DisplayNames !== 'function') return null;
  try {
    return new Intl.DisplayNames([locale], { type }).of(code) ?? null;
  } catch {
    return null;
  }
}

/** «Argentina», «Brasil»; fallback: the `COUNTRIES` name, else the code. */
export function regionName(code: string, locale: string): string {
  return (
    displayName('region', code, locale) ?? COUNTRIES_MAP.get(code)?.text ?? code
  );
}

/** «español», «portugués»; fallback: the code. */
export function languageName(code: string, locale: string): string {
  return displayName('language', code, locale) ?? code;
}

/** «a, b y c» in the locale; fallback: comma-joined. */
export function listJoin(items: string[], locale: string): string {
  const ListFormat: unknown = Reflect.get(Intl, 'ListFormat');
  if (isListFormatConstructor(ListFormat)) {
    try {
      return new ListFormat(locale, { type: 'conjunction' }).format(items);
    } catch {
      // Unsupported locale: fall through to the plain join.
    }
  }
  return items.join(', ');
}
